import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { buildClassName } from './buildClassName';
import { formatDrivingSpeed, isDrivingSpeedIdle, type VehicleState } from '../model/vehicleDriving';

export interface DrivingSpeedReadoutProps {
  stateRef: RefObject<VehicleState>;
  /**
   * 계기판이 0을 가리킬 때 "정지" 대신 띄울 문구.
   *
   * 키보드 조작 안내를 여기로 넣습니다. 세워 두는 동안에는 속도 숫자가 알려 줄 것이
   * 없고, 그 순간이 바로 어떻게 움직이는지 찾는 순간입니다. 안내를 따로 한 줄 세우면
   * 뷰어 세로를 36px 먹는데(알약 패딩 14px + 글자 16px + 바깥 gap 6px), 이렇게 두면
   * 한 픽셀도 더 쓰지 않습니다.
   *
   * 넘기지 않으면 "정지"가 그대로 나옵니다. 손가락으로만 조작하는 기기에서 그 기본값을
   * 씁니다 — 방향키도 스페이스도 없는 화면에서 키보드 안내는 읽어도 쓸 데가 없습니다.
   */
  idleLabel?: string;
}

/**
 * 주행 속도 표시.
 *
 * 운동 상태는 3D 루프가 ref에 쓰기 때문에(React state로 두면 60fps 리렌더가 됩니다)
 * 여기서 직접 읽어야 합니다. 자기 rAF 루프를 돌리고 **표시할 문자열이 바뀔 때만**
 * setState합니다. 그래서 다시 렌더되는 것은 이 작은 컴포넌트뿐이고, 가속하는 동안에도
 * 초당 열 번 남짓입니다(km/h 정수가 바뀌는 만큼).
 *
 * 이 컴포넌트를 따로 뺀 이유가 그것입니다. 상위(ShowroomPage)에서 상태로 들고 있으면
 * 같은 갱신마다 옵션 패널 전체가 함께 다시 렌더됩니다.
 */
export function DrivingSpeedReadout({ stateRef, idleLabel }: DrivingSpeedReadoutProps) {
  const resolveLabel = useCallback(() => {
    if (idleLabel !== undefined && isDrivingSpeedIdle(stateRef.current)) {
      return idleLabel;
    }
    return formatDrivingSpeed(stateRef.current);
  }, [stateRef, idleLabel]);

  const [label, setLabel] = useState(resolveLabel);

  /**
   * 정지 문구인지를 따로 state로 두지 않습니다. 두 값은 언제나 같은 프레임에 바뀌므로
   * state가 둘이면 같은 갱신에 렌더가 두 번 걸립니다. 문자열 비교로 되짚어도 안전한
   * 이유는 idleLabel이 속도 문구("18 km/h", "후진 18 km/h")와 겹칠 수 없기 때문입니다.
   */
  const isIdle = idleLabel !== undefined && label === idleLabel;

  useEffect(() => {
    let frame = 0;
    let shown = label;

    const tick = () => {
      const next = resolveLabel();
      if (next !== shown) {
        shown = next;
        setLabel(next);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // label을 의존성에 넣으면 갱신마다 루프를 다시 시작합니다. 초기값으로만 씁니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveLabel]);

  return (
    <span
      className={buildClassName(
        'driving-controls__speed',
        'driving-controls__speed--idle',
        isIdle
      )}
      role="status"
      aria-live="off"
    >
      {label}
    </span>
  );
}
